/**
 * Domain types.
 *
 * The central rule: **a measured observation and an inferred judgement are different
 * types.** `FaceObservation` can only hold what a sensor reported. `AttentionInference` is
 * the only type allowed to express a judgement, and `createInference` refuses to build one
 * without a confidence value and at least one piece of evidence.
 *
 * That is how §40 stays enforceable in code rather than in review comments: an unexplained
 * verdict is not constructible, so nothing downstream can display one.
 *
 * Zero dependencies — this module and everything built on it runs under plain Node with no
 * DOM, no WASM and no native modules, which is what makes the logic testable.
 */

// ---------------------------------------------------------------------------------------
// Vocabularies
// ---------------------------------------------------------------------------------------

/** Approximate head orientation relative to the calibrated workstation-facing pose. */
export const DIRECTIONS = [
  'forward',
  'left',
  'right',
  'up',
  'down',
  'strongly_away',
  'not_detected',
] as const;
export type Direction = (typeof DIRECTIONS)[number];

/**
 * Temporal attention state.
 *
 * `unknown` and `insufficient_data` are first-class outcomes, not error cases: §20 requires
 * that uncertainty never be converted into a negative signal about a person, so the engine
 * must always have a truthful state available to it.
 */
export const ATTENTION_STATES = [
  'focused',
  'possibly_distracted',
  'away',
  'idle',
  'meeting',
  'unknown',
  'insufficient_data',
] as const;
export type AttentionState = (typeof ATTENTION_STATES)[number];

/** Duration band for an attention-away episode (§11). Band edges are configurable. */
export const EVENT_SEVERITIES = [
  'ignored',
  'minor_shift',
  'attention_away',
  'potentially_significant',
] as const;
export type EventSeverity = (typeof EVENT_SEVERITIES)[number];

export const EVENT_TYPES = [
  'attention_away',
  'face_lost',
  'quality_degraded',
  'state_change',
  'session_start',
  'session_end',
  'calibration_saved',
] as const;
export type EventType = (typeof EVENT_TYPES)[number];

/** Why a frame may not support a trustworthy inference (§20). */
export const QUALITY_FLAGS = [
  'too_dark',
  'too_bright',
  'blurry',
  'face_too_small',
  'multiple_faces',
  'no_face',
  'low_landmark_confidence',
  'high_reprojection_error',
  'camera_unavailable',
] as const;
export type QualityFlag = (typeof QUALITY_FLAGS)[number];

const INCONCLUSIVE: ReadonlySet<AttentionState> = new Set<AttentionState>([
  'unknown',
  'insufficient_data',
]);

/** True when the state expresses a finding rather than an absence of information. */
export function isConclusive(state: AttentionState): boolean {
  return !INCONCLUSIVE.has(state);
}

export function isWorkstationFacing(direction: Direction): boolean {
  return direction === 'forward';
}

/** False when there is no orientation at all, as opposed to one pointing away. */
export function isMeasurable(direction: Direction): boolean {
  return direction !== 'not_detected';
}

// ---------------------------------------------------------------------------------------
// Measured values
// ---------------------------------------------------------------------------------------

/**
 * Head rotation in degrees.
 *
 * Sign convention, fixed across the codebase and asserted in `test/headPose.test.ts`
 * against synthetic projections of known rotations:
 *
 * - `yaw`   negative = subject turned toward **their own** left (which appears on the
 *   right-hand side of an unmirrored camera image); positive = their right.
 * - `pitch` negative = looking down, positive = looking up.
 * - `roll`  positive = head tipped toward the subject's right shoulder.
 *
 * The convention is employee-centric, not image-centric, so `left` means the same thing
 * here as it does in a multi-monitor policy ("facing left -> monitor 3", §14). Getting this
 * backwards would mirror every direction statistic the system ever produces.
 */
export interface HeadPose {
  readonly yaw: number;
  readonly pitch: number;
  readonly roll: number;
}

export function headPose(yaw: number, pitch: number, roll: number): HeadPose {
  for (const [name, value] of [
    ['yaw', yaw],
    ['pitch', pitch],
    ['roll', roll],
  ] as const) {
    if (!Number.isFinite(value)) {
      throw new RangeError(`HeadPose.${name} must be a finite number, got ${String(value)}`);
    }
  }
  return { yaw, pitch, roll };
}

/** Express a pose relative to a calibrated neutral pose. */
export function offsetPose(pose: HeadPose, neutral: HeadPose): HeadPose {
  return {
    yaw: pose.yaw - neutral.yaw,
    pitch: pose.pitch - neutral.pitch,
    roll: pose.roll - neutral.roll,
  };
}

/** Per-frame usability metrics. Produced by the preprocessor, never by a model. */
export interface FrameQuality {
  readonly brightness: number;
  readonly blurVariance: number;
  readonly faceBoxRatio: number | null;
  readonly flags: readonly QualityFlag[];
}

export function isFrameUsable(quality: FrameQuality): boolean {
  return quality.flags.length === 0;
}

/**
 * True when no face is in frame, or the camera itself is gone.
 *
 * Deliberately distinct from "a face is visible but its orientation could not be measured".
 * Treating the latter as absence would record an employee as having left their desk because
 * the pose solver had a bad second.
 */
export function isFaceAbsent(quality: FrameQuality): boolean {
  return quality.flags.includes('no_face') || quality.flags.includes('camera_unavailable');
}

/**
 * Normalised facial landmarks from a `LandmarkProvider`.
 *
 * Points are `[x, y, z]` in normalised image space (0..1 for x/y). Landmarks are consumed
 * within the frame that produced them and are never persisted or transmitted — see the
 * privacy manifest.
 */
export interface FaceLandmarks {
  readonly points: readonly (readonly [number, number, number])[];
  readonly imageWidth: number;
  readonly imageHeight: number;
  readonly detectionConfidence: number;
  readonly faceCount: number;
}

export function landmarkPixel(
  landmarks: FaceLandmarks,
  index: number,
): readonly [number, number] {
  const point = landmarks.points[index];
  if (point === undefined) {
    throw new RangeError(
      `Landmark index ${index} is out of range (provider returned ${landmarks.points.length} points)`,
    );
  }
  return [point[0] * landmarks.imageWidth, point[1] * landmarks.imageHeight];
}

/** Face height as a fraction of frame height — a proxy for how far away the subject is. */
export function faceBoundingBoxRatio(landmarks: FaceLandmarks): number {
  if (landmarks.points.length === 0) return 0;
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const [, y] of landmarks.points) {
    if (y < min) min = y;
    if (y > max) max = y;
  }
  return Math.max(0, Math.min(1, max - min));
}

/** MEASURED per-frame result. Carries no judgement about the employee. */
export interface FaceObservation {
  readonly timestampUtc: Date;
  readonly monotonicTs: number;
  readonly faceDetected: boolean;
  readonly faceCount: number;
  readonly quality: FrameQuality;
  readonly headPose: HeadPose | null;
  readonly detectionConfidence: number;
  readonly poseConfidence: number;
  readonly reprojectionErrorPx: number | null;
}

/**
 * Keyboard/mouse/application context (Phase 2).
 *
 * Phase 1 passes `null` for activity throughout. The state engine treats absent activity as
 * *absent evidence* rather than as inactivity, which is why this type exists now: it makes
 * "we did not measure this" different from "there was none".
 *
 * Only metadata is ever carried. There is no field capable of holding keystroke content,
 * and adding one would be a visible breaking change.
 */
export interface ActivitySignals {
  readonly keyboardActivity: boolean;
  readonly mouseActivity: boolean;
  readonly keyboardEventsPerMinute: number;
  readonly mouseEventsPerMinute: number;
  readonly idleDurationSeconds: number;
  readonly applicationCategory: string | null;
  readonly workRelevant: boolean | null;
  readonly meetingActive: boolean;
}

// ---------------------------------------------------------------------------------------
// Inferred values
// ---------------------------------------------------------------------------------------

/** A smoothed, calibrated direction reading — the input to the state engine. */
export interface DirectionSample {
  readonly monotonicTs: number;
  readonly timestampUtc: Date;
  readonly direction: Direction;
  readonly confidence: number;
  readonly smoothedPose: HeadPose | null;
  readonly rawPose: HeadPose | null;
  readonly reasons: readonly string[];
  readonly quality: FrameQuality | null;
}

/** INFERRED attention state. Cannot exist without confidence and evidence. */
export interface AttentionInference {
  readonly timestampUtc: Date;
  readonly monotonicTs: number;
  readonly state: AttentionState;
  readonly confidence: number;
  readonly direction: Direction;
  readonly evidence: readonly string[];
  readonly smoothedPose: HeadPose | null;
  readonly stateDurationSeconds: number;
}

export function createInference(input: AttentionInference): AttentionInference {
  if (!(input.confidence >= 0 && input.confidence <= 1)) {
    throw new RangeError(`confidence must be within [0, 1], got ${String(input.confidence)}`);
  }
  if (input.evidence.length === 0) {
    throw new Error(
      'AttentionInference requires at least one evidence string: an inference that cannot ' +
        'be explained must not be representable (§30, §40).',
    );
  }
  return input;
}

/**
 * A completed, debounced episode worth recording.
 *
 * This is the only shape sent to the backend for attention data. Note the absence of any
 * image, landmark or biometric field.
 */
export interface AttentionEvent {
  readonly eventType: EventType;
  readonly sessionId: string;
  readonly startTimeUtc: Date;
  readonly endTimeUtc: Date;
  readonly durationSeconds: number;
  readonly direction: Direction;
  readonly severity: EventSeverity;
  readonly confidence: number;
  readonly evidence: readonly string[];
  readonly agentVersion: string;
  readonly modelVersion: string;
  readonly configVersion: string;
  readonly metadata: Readonly<Record<string, unknown>>;
}

export function createEvent(input: AttentionEvent): AttentionEvent {
  if (input.durationSeconds < 0) {
    throw new RangeError('durationSeconds must not be negative');
  }
  if (!(input.confidence >= 0 && input.confidence <= 1)) {
    throw new RangeError(`confidence must be within [0, 1], got ${String(input.confidence)}`);
  }
  return input;
}

/** Aggregates required by §11. `eventsPerHour` is null when the window is too short. */
export interface AttentionEventStats {
  readonly eventCount: number;
  readonly totalDurationSeconds: number;
  readonly averageDurationSeconds: number;
  readonly longestDurationSeconds: number;
  readonly eventsPerHour: number | null;
  readonly directionDistribution: Readonly<Record<string, number>>;
  readonly severityDistribution: Readonly<Record<string, number>>;
  readonly observedWindowSeconds: number;
  readonly meanConfidence: number;
}

// ---------------------------------------------------------------------------------------
// Wire serialisation
// ---------------------------------------------------------------------------------------

/** RFC3339 UTC with milliseconds — the wire format for every timestamp. */
export function isoTimestamp(value: Date): string {
  return value.toISOString();
}

function round(value: number, places: number): number {
  const factor = 10 ** places;
  return Math.round(value * factor) / factor;
}

export function headPoseToJson(pose: HeadPose): Record<string, number> {
  return { yaw: round(pose.yaw, 2), pitch: round(pose.pitch, 2), roll: round(pose.roll, 2) };
}

export function frameQualityToJson(quality: FrameQuality): Record<string, unknown> {
  return {
    brightness: round(quality.brightness, 1),
    blur_variance: round(quality.blurVariance, 1),
    face_box_ratio: quality.faceBoxRatio === null ? null : round(quality.faceBoxRatio, 4),
    flags: [...quality.flags],
  };
}

/** The §4 example event shape. Snake_case on the wire, camelCase in the code. */
export function observationToJson(observation: FaceObservation): Record<string, unknown> {
  return {
    timestamp: isoTimestamp(observation.timestampUtc),
    face_detected: observation.faceDetected,
    face_count: observation.faceCount,
    head_pose: observation.headPose === null ? null : headPoseToJson(observation.headPose),
    face_detection_confidence: round(observation.detectionConfidence, 3),
    head_pose_confidence: round(observation.poseConfidence, 3),
    reprojection_error_px:
      observation.reprojectionErrorPx === null ? null : round(observation.reprojectionErrorPx, 2),
    quality: frameQualityToJson(observation.quality),
  };
}

export function inferenceToJson(inference: AttentionInference): Record<string, unknown> {
  return {
    timestamp: isoTimestamp(inference.timestampUtc),
    state: inference.state,
    confidence: round(inference.confidence, 3),
    direction: inference.direction,
    state_duration_seconds: round(inference.stateDurationSeconds, 1),
    head_pose: inference.smoothedPose === null ? null : headPoseToJson(inference.smoothedPose),
    evidence: [...inference.evidence],
  };
}

export function eventToJson(event: AttentionEvent): Record<string, unknown> {
  return {
    event_type: event.eventType,
    session_id: event.sessionId,
    start_time: isoTimestamp(event.startTimeUtc),
    end_time: isoTimestamp(event.endTimeUtc),
    duration_seconds: round(event.durationSeconds, 2),
    direction: event.direction,
    severity: event.severity,
    confidence: round(event.confidence, 3),
    evidence: [...event.evidence],
    agent_version: event.agentVersion,
    model_version: event.modelVersion,
    config_version: event.configVersion,
    metadata: { ...event.metadata },
  };
}

export function eventStatsToJson(stats: AttentionEventStats): Record<string, unknown> {
  return {
    event_count: stats.eventCount,
    total_duration_seconds: round(stats.totalDurationSeconds, 1),
    average_duration_seconds: round(stats.averageDurationSeconds, 2),
    longest_duration_seconds: round(stats.longestDurationSeconds, 2),
    events_per_hour: stats.eventsPerHour === null ? null : round(stats.eventsPerHour, 2),
    direction_distribution: { ...stats.directionDistribution },
    severity_distribution: { ...stats.severityDistribution },
    observed_window_seconds: round(stats.observedWindowSeconds, 1),
    mean_confidence: round(stats.meanConfidence, 3),
  };
}

/** Neutral, employee-facing wording (§35). Describes observations, never the person. */
export const STATE_LABEL: Readonly<Record<AttentionState, string>> = {
  // "At workstation", not "Facing workstation": this state is also correct during a brief
  // glance elsewhere, and pairing the headline "Facing workstation" with the direction
  // "oriented upward" reads as a contradiction to the person looking at it.
  focused: 'At workstation',
  possibly_distracted: 'Attention away from workstation',
  away: 'Away from workstation',
  idle: 'Low computer interaction',
  meeting: 'In a meeting',
  unknown: 'Unknown',
  insufficient_data: 'Insufficient data',
};

export const DIRECTION_LABEL: Readonly<Record<Direction, string>> = {
  forward: 'facing workstation',
  left: 'oriented to their left',
  right: 'oriented to their right',
  up: 'oriented upward',
  down: 'oriented downward',
  strongly_away: 'oriented well away from the workstation',
  not_detected: 'orientation not measurable',
};

export function clamp(value: number, low: number, high: number): number {
  return Math.max(low, Math.min(high, value));
}

/**
 * Combine independent confidence factors into one.
 *
 * **Geometric mean, deliberately not a product.** Multiplying independent penalties compounds
 * them: four factors of 0.7 multiply to 0.24, so a reading that is merely "somewhat imperfect
 * in four respects" collapses below any usable floor. That is exactly what happened in the
 * first version of this pipeline — a cleanly detected head at 60° yaw, in good light, scored
 * 0.23 and was reported as `unknown`, because pose geometry, angle range, detector presence
 * and temporal stability were each individually fine but multiplied to nothing. The system
 * became least willing to say anything precisely when the head was furthest from centre,
 * which is when its answer matters most.
 *
 * The geometric mean averages the factors in log space instead: four factors of 0.7 give 0.7.
 * It keeps the two properties that matter — 1 when everything is perfect, and 0 when any
 * single factor is disqualifying — while letting a mediocre factor lower the result rather
 * than annihilate it.
 */
export function combineConfidence(...factors: readonly number[]): number {
  if (factors.length === 0) return 0;
  let logSum = 0;
  for (const factor of factors) {
    const bounded = clamp(factor, 0, 1);
    // A zero factor is disqualifying, not merely bad: no face, several faces, or an image
    // that cannot support a measurement at all.
    if (bounded <= 0) return 0;
    logSum += Math.log(bounded);
  }
  return clamp(Math.exp(logSum / factors.length), 0, 1);
}
